import { useEffect, useState } from 'react';
import { ModelPicker } from './ModelPicker';
import { useBench } from './quick/useBench';
import { sayQuote } from '../lib/quick';
import { api, type ModelInfo, voiceLabel, VOICES, type Asset, type Brand, type Idea, type TalentRow, type Voice } from '../lib/api';
import { LOOK_EVERY_MS, GIVE_UP_AFTER, GAVE_UP, useElapsed, saySeconds } from '../lib/waiting';

// The Audio Lab (196): sources in, audio assets out.
//
// Three benches. VOICE speaks lines in one voice — a talent's, or a bare one
// from the catalogue — and files the speech in the library, which is the
// thing speech could never be before: it lived inside a render, in a
// preview's memory, or as the podcast copy of a finished cut. MUSIC is the
// bed bench, moved here from the library's toolbar. SOUND waits on a model
// that makes effects, and says so rather than offering a button that does
// not. Everything a bench makes is an ordinary audio asset: nothing a lab
// makes is a special case in the library.
//
// The picker on the left is the cast, not a references strip: that is what
// this studio has that a generic tool does not — the people persist.

type Bench = 'voice' | 'music' | 'sound';
interface Line { text: string; read: string }

const MAX_LINES = 12;

export function AudioLab({ brand, prefill, onUsedPrefill }: {
  brand: Brand | null;
  /** A bed brief handed over — from the Idea Lab or the Quick Studio's Music tab — read once by the music bench. */
  prefill?: Idea | null;
  onUsedPrefill?: () => void;
}) {
  // A bed prefill means the music bench, so open on it rather than making
  // the creator find it after they already said yes.
  const bedPrefill = Boolean(prefill && (prefill.action === 'compose_bed' || prefill.action === 'make_music'));
  const [bench, setBench] = useState<Bench>(bedPrefill ? 'music' : 'voice');
  useEffect(() => { if (bedPrefill) setBench('music'); }, [bedPrefill]);
  const [talent, setTalent] = useState<TalentRow[]>([]);
  const [voices, setVoices] = useState<Voice[]>(VOICES.map((v) => ({ id: v.id, label: v.label, tier: 'local' as const, language: v.id.slice(0, 5).replace('_', '-'), accent: v.hint })));
  const [made, setMade] = useState<Asset[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [playing, setPlaying] = useState<Record<string, string>>({});

  useEffect(() => {
    let live = true;
    api.talent().then((r) => { if (live) setTalent(r.talent); }).catch(() => {});
    api.voices().then((list) => { if (live && list.length) setVoices(list); }).catch(() => {});
    return () => { live = false; };
  }, []);

  // What this bench has made, for the two groups it files into. A made
  // asset is promoting until the render plane has looked at it, exactly as
  // an upload is; the list is watched while any is, and stops when none.
  async function loadMade() {
    try {
      const all = await api.assets(brand?.id ?? null);
      setMade(all.filter((a) => a.kind === 'audio' && (a.group_name === 'Voice' || a.group_name === 'Music')));
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void loadMade(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [brand?.id]);
  const [stalled, setStalled] = useState(false);
  const promoting = made.some((a) => a.status === 'promoting' || a.status === 'pending');
  useEffect(() => {
    if (!promoting) { setStalled(false); return; }
    let looks = 0;
    const t = setInterval(() => { if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); setStalled(true); return; } void loadMade(); }, LOOK_EVERY_MS);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoting]);

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key); setError(null); setNote(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }
  async function play(a: Asset) {
    if (playing[a.id]) { setPlaying((m) => { const { [a.id]: _x, ...rest } = m; return rest; }); return; }
    try { const { url } = await api.assetUrl(a.id); setPlaying((m) => ({ ...m, [a.id]: url })); } catch (e) { setError((e as Error).message); }
  }

  return (
    <section data-tool="audio-lab" style={{ display: 'grid', gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>The Audio Lab</h3>
        <span className="sub-dim">lines in a cast's voice, a bed from a brief — into the library{brand ? `, for ${brand.name}` : ''}</span>
        <span style={{ flex: 1 }} />
        {(['voice', 'music', 'sound'] as Bench[]).map((b) => (
          <button key={b} type="button" className={`btn tiny${bench === b ? ' pri' : ''}`} onClick={() => setBench(b)}>{b === 'voice' ? 'Voice' : b === 'music' ? 'Music' : 'Sound'}</button>
        ))}
      </div>
      {error && <p className="err" role="alert" style={{ margin: 0 }}>{error}</p>}
      {note && <p className="ok" style={{ margin: 0 }}>{note}</p>}

      {bench === 'voice' && <VoiceBench brand={brand} talent={talent} voices={voices} busy={busy} onRun={run} onMade={(m) => { setNote(m); void loadMade(); }} />}
      {bench === 'music' && <MusicBench brand={brand} busy={busy} onRun={run} onMade={(m) => { setNote(m); void loadMade(); }} prefill={prefill} onUsedPrefill={onUsedPrefill} />}
      {bench === 'sound' && (
        <p className="sub-dim" style={{ margin: 0 }} data-tool="audio-sound-waits">
          Sound effects wait on a model that makes them well. The music model makes music; a bench that returned a thirty-second bed when you asked for a door closing would be worse than none. Voice and Music are here now.
        </p>
      )}

      <div style={{ display: 'grid', gap: 6 }} data-tool="audio-made">
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Made here</h4>
          <span className="sub-dim" style={{ fontSize: 12 }}>{made.length ? `${made.length} in the library under Voice and Music` : 'nothing yet'}</span>
          {promoting && !stalled && <span className="sub-dim" style={{ fontSize: 12 }}>· the render plane is looking at the newest, about a minute</span>}
          {stalled && <span className="sub-dim" style={{ fontSize: 12 }}>· {GAVE_UP} <button type="button" className="btn tiny" onClick={() => { setStalled(false); void loadMade(); }}>Look again</button></span>}
        </div>
        {made.slice(0, 12).map((a) => (
          <div key={a.id} className="row" style={{ gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--line)' }}>
            <span className="chip">{a.group_name}</span>
            <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.description ?? ''}>{a.name ?? a.storage_key.split('/').pop()}</span>
            <span className="sub-dim" style={{ fontSize: 12 }}>{a.duration_ms ? saySeconds(Math.round(a.duration_ms / 1000)) : ''}</span>
            {a.status === 'ready'
              ? <button type="button" className="btn tiny" onClick={() => void play(a)}>{playing[a.id] ? 'Stop' : 'Play'}</button>
              : <span className="sub-dim" style={{ fontSize: 12 }}>{a.status === 'error' ? 'failed to process' : 'developing…'}</span>}
            {playing[a.id] && <audio src={playing[a.id]} autoPlay controls style={{ height: 28 }} onEnded={() => setPlaying((m) => { const { [a.id]: _x, ...rest } = m; return rest; })} />}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Lines in one voice, filed as one asset. */
function VoiceBench({ brand, talent, voices, busy, onRun, onMade }: {
  brand: Brand | null; talent: TalentRow[]; voices: Voice[]; busy: string | null;
  onRun: (key: string, fn: () => Promise<void>) => Promise<void>; onMade: (note: string) => void;
}) {
  const [who, setWho] = useState<string>('');        // a talent id, or `voice:<id>`
  const [lines, setLines] = useState<Line[]>([{ text: '', read: '' }]);
  const [name, setName] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);
  const speaking = busy === 'speak';
  useEffect(() => { if (!speaking && startedAt !== null) setStartedAt(null); }, [speaking, startedAt]);

  const chosen = who.startsWith('voice:') ? { voiceId: who.slice(6) } : who ? { talentId: who } : null;
  const chosenTalent = talent.find((t) => t.id === who);
  const voiceless = Boolean(chosenTalent && !chosenTalent.voice_id);
  const said = lines.map((l) => l.text.trim()).filter(Boolean);
  const chars = said.reduce((n, t) => n + t.length, 0);
  const canSpeak = Boolean(chosen) && !voiceless && said.length > 0 && !busy;
  const voiceOf = (id: string) => voiceLabel(id, voices);

  const groups = (() => {
    const out = new Map<string, Voice[]>();
    for (const v of voices) {
      const key = v.tier === 'local' ? 'In the render image' : v.tier === 'directed' ? 'Directed — reads the way you tell it' : `${v.accent} · ${v.language}`;
      out.set(key, [...(out.get(key) ?? []), v]);
    }
    return [...out.entries()];
  })();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)', gap: 14 }} data-tool="audio-voice">
      {/* Whose voice. The cast first, because it is the point; a bare voice for a line nobody in the vault says. */}
      <div style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
        <span className="sub-dim">Whose voice</span>
        {talent.length === 0 && <p className="sub-dim" style={{ margin: 0, fontSize: 12 }}>Nobody in the vault yet — add talent, or pick a voice below.</p>}
        {talent.map((t) => (
          <button key={t.id} type="button" className={`btn tiny${who === t.id ? ' pri' : ''}`} style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  onClick={() => setWho(t.id)} title={t.voice_id ? voiceOf(t.voice_id) : 'no voice yet — give them one on Talent'}>
            {t.name}<span className="sub-dim" style={{ marginLeft: 6, fontSize: 11 }}>{t.voice_id ? voiceOf(t.voice_id) : 'no voice'}</span>
          </button>
        ))}
        <label className="sub-dim" style={{ fontSize: 12, marginTop: 6 }}>Or a voice
          <select value={who.startsWith('voice:') ? who.slice(6) : ''} onChange={(e) => setWho(e.target.value ? `voice:${e.target.value}` : '')} style={{ width: '100%' }}>
            <option value="">choose</option>
            {groups.map(([g, vs]) => <optgroup key={g} label={g}>{vs.map((v) => <option key={v.id} value={v.id}>{v.label}{v.hint ? ` — ${v.hint}` : ''}</option>)}</optgroup>)}
          </select>
        </label>
      </div>

      <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
        {lines.map((l, i) => (
          <div key={i} className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
            <textarea value={l.text} rows={2} maxLength={1500} style={{ flex: 1 }} placeholder={i === 0 ? 'The line — "Dinner is ready, and I am not sharing."' : 'The next line'}
                      onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
            <input value={l.read} maxLength={200} style={{ width: 150 }} placeholder="read it… wearily" title="How to read it — honoured by a voice that takes direction, ignored by the rest"
                   onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, read: e.target.value } : x)))} />
            {lines.length > 1 && <button type="button" className="btn tiny" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>×</button>}
          </div>
        ))}
        <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn tiny" disabled={lines.length >= MAX_LINES} onClick={() => setLines((ls) => [...ls, { text: '', read: '' }])}>+ line</button>
          <input value={name} maxLength={200} placeholder="Name in the library (optional)" onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
          <button type="button" className="btn pri" disabled={!canSpeak} data-move="audio-voice-quote"
                  title={!chosen ? 'Say whose voice' : voiceless ? `${chosenTalent?.name} has no voice yet — give them one on Talent` : said.length === 0 ? 'Write a line' : `${chars} characters in ${said.length} ${said.length === 1 ? 'line' : 'lines'}`}
                  onClick={() => { setStartedAt(Date.now()); void onRun('speak', async () => {
                    const r = await api.speak({ lines: lines.filter((x) => x.text.trim()).map((x) => ({ text: x.text.trim(), ...(x.read.trim() ? { read: x.read.trim() } : {}) })), ...chosen!, brandId: brand?.id ?? null, ...(name.trim() ? { name: name.trim() } : {}) });
                    setLines([{ text: '', read: '' }]); setName('');
                    onMade(`Spoken — ${saySeconds(Math.round(r.seconds))}${r.chars ? `, ${r.chars.toLocaleString()} characters of cloud voice` : ', a local voice, so nothing billed'}. It is in the library under Voice once the render plane has looked at it.`);
                  }); }}>
            {speaking ? `Speaking… ${saySeconds(waited)}` : 'Speak it'}
          </button>
        </div>
        <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="audio-voice-quote">
          {voiceless ? `${chosenTalent?.name} has no voice yet. Give them one on the Talent screen and come back.`
            : who.startsWith('voice:en_') ? 'A local voice: free, and said by the render image.'
            : chosen ? `A cloud voice: ${chars.toLocaleString()} characters against this month's voice allowance, billed once the file is stored.`
            : 'Pick who says it. One voice per file; lines are joined with a short breath between.'}
        </p>
      </div>
    </div>
  );
}

/** A bed from a brief. Moved here from the library's toolbar, where it was a form beside "make a picture". */
function MusicBench({ brand, busy, onRun, onMade, prefill, onUsedPrefill }: {
  brand: Brand | null; busy: string | null; onRun: (key: string, fn: () => Promise<void>) => Promise<void>; onMade: (note: string) => void;
  prefill?: Idea | null; onUsedPrefill?: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [avoid, setAvoid] = useState('');
  // 204: the model the bed is composed on and the server's quote for one.
  const [musicModels, setMusicModels] = useState<ModelInfo[] | null>(null);
  useEffect(() => { let live = true; api.models('music').then((m) => { if (live) setMusicModels(m); }).catch(() => undefined); return () => { live = false; }; }, []);
  const bench = useBench('music', musicModels ?? undefined, () => ({ seconds: 30 }));
  // The brief fills the form once and is handed back: a prefill that
  // lingered would refill the bench the next time the creator walked past.
  useEffect(() => {
    if (!prefill || (prefill.action !== 'compose_bed' && prefill.action !== 'make_music')) return;
    const p = String(prefill.args.prompt ?? ''); const a = String(prefill.args.avoid ?? '');
    if (p) setPrompt(p);
    if (a) setAvoid(a);
    onUsedPrefill?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);
  const composing = busy === 'bed';
  useEffect(() => { if (!composing && startedAt !== null) setStartedAt(null); }, [composing, startedAt]);
  return (
    <div style={{ display: 'grid', gap: 8 }} data-tool="audio-music">
      <ModelPicker bench="music" models={musicModels} value={bench.model} onChange={bench.setModel} />
      <textarea value={prompt} rows={2} maxLength={500} placeholder="The bed — Asian folk, bamboo flute, gentle, under dialogue" onChange={(e) => setPrompt(e.target.value)} />
      <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
        <input value={avoid} maxLength={200} placeholder="Keep out — drums, vocals" onChange={(e) => setAvoid(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
        <button type="button" className="btn pri" disabled={Boolean(busy) || prompt.trim().length < 5} data-move="audio-music"
                onClick={() => { setStartedAt(Date.now()); void onRun('bed', async () => {
                  const r = await api.generateBed(prompt.trim(), brand?.id ?? null, { ...(bench.model ? { model: bench.model.id } : {}), ...(avoid.trim() ? { avoid: avoid.trim() } : {}) });
                  setPrompt(''); setAvoid('');
                  onMade(`Composed ${saySeconds(Math.round(r.seconds))} of music. It is in the library under Music once the render plane has looked at it.`);
                }); }}>
          {composing ? `Composing… ${saySeconds(waited)}` : bench.credits === null ? 'Compose' : `Compose · ${bench.credits.toLocaleString()} cr`}
        </button>
      </div>
      <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="audio-music-quote">
        {bench.credits === null ? 'Reading the price list…' : sayQuote(bench.credits, 'a 30-second bed', bench.quote?.balance)} On the paid plans. The mix on any episode can pick it up.
      </p>
    </div>
  );
}
